import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatTabChangeEvent, MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { DocumentsComponent } from './documents/documents.component';
import { EndorsementsComponent } from './endorsements/endorsements.component';
import { PolicyDocumentsComponent } from './policy-documents/policy-documents.component';
import { PricingComponent } from './pricing/pricing.component';
import { SummaryComponent } from './summary/summary.component';
import {
  ViewSubmissionStore,
  SubmissionSummaryViewModel,
  LookupStore,
  LookupViewModel,
} from '@program-suite/data-common';
import {
  MenuItem,
  ButtonComponent,
  ToolbarComponent,
  ModalService,
  ModalType,
  ArchiveSubmissionModalComponent,
  SnackbarComponent,
  ToastType,
  UpdateProducerModalComponent,
  BindRequestComponent,
} from '@program-suite/ui';
import { AuthStore } from '@program-suite/data-auth';
import { SubmissionStatusEnum } from '@program-suite/data-common';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  PolicyEndorsementFormComponent,
  CoverageAdjustmentFormComponent,
} from '@program-suite/feature-policy-endorsements';
import { SubmissionReviewModalComponent } from './submission-review-modal/submission-review-modal.component';
import { PriorWorkExclusionModalComponent } from '@program-suite/modals';
import { LossRunsComponent } from './loss-runs/loss-runs.component';

enum SubmissionTabs {
  summary = 0,
  pricing = 1,
  documents = 2,
}

enum PolicyTabs {
  summary = 0,
  pricing = 1,
  policydocuments = 2,
  endorsements = 3,
  lossruns = 4,
  documents = 5,
}

// LossRunProcessor only sees these two tabs on a policy
enum LossRunProcessorTabs {
  lossruns = 0,
  documents = 1,
}

@Component({
  selector: 'lib-view-submission',
  templateUrl: './view-submission.component.html',
  styleUrl: './view-submission.component.scss',
  imports: [
    MatTabsModule,
    DocumentsComponent,
    EndorsementsComponent,
    PolicyDocumentsComponent,
    PricingComponent,
    SummaryComponent,
    ButtonComponent,
    ButtonComponent,
    RouterModule,
    ToolbarComponent,
    LossRunsComponent
],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
// Todo: Make switch cases for the conditional button logic (titles and actions). See SummaryComponent for example.
export class ViewSubmissionComponent implements OnInit, OnDestroy {
  viewSubmissionStore = inject(ViewSubmissionStore);
  authStore = inject(AuthStore);
  lookupStore = inject(LookupStore);
  roles = computed(() => this.authStore.user()?.roles ?? []);
  summary = computed<SubmissionSummaryViewModel>(() => {
    return this.viewSubmissionStore.summary?.() ?? {};
  });
  agencyId = computed(() => {
    const agencies = this.lookupStore.agencies?.() ?? [];
    return agencies.find((a) => a.name === this.summary()?.agencyName)?.id;
  });
  // LIO is edited in its own wizard (see editQuote), not the RCIP one.
  isLio = computed(() => this.summary()?.programName === 'LIO');

  tabGroup = viewChild<MatTabGroup>('tabGroup');
  tab: string | null = null;
  id: string | null = null;
  baseUrl = '';
  isPolicyPage: boolean = false;
  tabEnum!: typeof SubmissionTabs | typeof PolicyTabs | typeof LossRunProcessorTabs;
  loading = signal(false);
  backText = signal('');

  // Main button signals
  mainSplitButtonText = computed(() => {
    var acceptedStatuses = ['Indication', 'Underwriter Review Quote', 'Underwriter Review Bind', 'Producer Review Quote', 'Producer Review Bind'];
    if (this.authStore.isAdmin()) {
      if (this.summary()?.status?.includes('Declined')) {
        return 'Review';
      }
      return 'Manage';
    } else if (acceptedStatuses.includes(this.summary()?.status ?? '')) {
      return 'Manage';
    }
    return 'Review';
  });
  mainSplitButtonAction = computed(() => {
    const buttonText = this.mainSplitButtonText();

    if (buttonText === 'Manage') {
      return this.editQuote();
    } else {
      //if (buttonText === 'Review')
      return this.reviewSubmission();
    }
  });

  // Dropdown menu items
  submissionMenuItems = computed<MenuItem[]>(() => [
    {
      name: this.authStore.isAdmin() ? 'Issue Policy' : 'Bind Request',
      type: 'button',
      disabled: this.menuItemDisabled(this.authStore.isAdmin() ? 'Issue Policy' : 'Bind Request'),
      action: () => this.firstSubmissionMenuItemAction(),
    },
    {
      name: 'Update Producer',
      type: 'button',
      disabled: this.summary()?.status?.includes('Declined') && !this.authStore.isAdmin(),
      action: () => this.updateProducer(),
    },
    {
      name: 'Archive',
      type: 'button',
      action: () => this.archive(),
    },
  ]);

  firstSubmissionMenuItemAction() {
    const issuableStatus = ['Quoted', 'Submitted', 'Bind Request', 'Underwriter Review', 'Producer Review'];
    const status = this.summary()?.status ?? '';
    if (this.authStore.isAdmin() && issuableStatus.includes(status)) {
      this.issuePolicy();
    } else {
      this.bindRequest();
    }
  }

  policyMenuItems = computed<MenuItem[]>(() => {
    var items: MenuItem[] = [
      {
        name: 'Coverage Adjustment',
        type: 'button',
        action: () =>
          this.router.navigate(
            ['/policies', 'endorsements', this.summary()?.policyGuid, 'coverage-adjustment', 'new'],
            {
              state: {
                policyGuid: this.id,
                policyNumber: this.summary()?.policyNumber,
                businessName: this.summary()?.businessInfo?.businessName,
                subTitle: `${this.summary()?.businessInfo?.businessName} | ${this.summary()?.policyNumber}`,
                mode: 'add',
              },
            },
          ),
      },
      {
        name: 'Additional Insured',
        type: 'button',
        action: () =>
          this.router.navigate(['/policies', 'endorsements', this.summary()?.policyGuid, 'additional-insured', 'new'], {
            state: {
              policyNumber: this.summary()?.policyNumber,
              businessName: this.summary()?.businessInfo?.businessName,
              policyGuid: this.summary()?.policyGuid,
            },
          }),
        disabled: this.summary()?.status == 'Cancelled',
      },
      {
        name: 'Cancellation',
        type: 'button',
        disabled: this.summary()?.status == 'Cancelled',
        action: () => this.openAddEndorsementModal('Cancellation'),
      },
      {
        name: 'Reinstatement',
        type: 'button',
        disabled: this.summary()?.status != 'Cancelled',
        action: () => this.openAddEndorsementModal('Reinstatement'),
      },
    ];
    if (this.authStore.isAdmin()) {
      items.push({ name: 'Other Endorsement', type: 'button', action: () => this.openAddEndorsementModal('Other') });
    }
    return items;
  });

  // Toolbar
  notificationIcon = 'file-invoice-dollar';
  submissionsIcon = 'address-card';
  changeRequestsIcon = 'calendar-clock';

  toolbarTiles = [
    { iconName: this.notificationIcon, label: '', data: '' },
    { iconName: this.submissionsIcon, label: '', data: '' },
    { iconName: this.changeRequestsIcon, label: '', data: '' },
  ];

  toolbarClick(tile: any) {
    console.log('Toolbar tile clicked:', tile);
  }

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private router: Router,
    private modalService: ModalService,
    private snackbar: MatSnackBar,
  ) {
    effect(() => {
      if (this.baseUrl?.toLowerCase() == 'submissions') {
        if (this.summary()?.policyGuid) {
          //route to policy page if policyGuid is present
          this.router.navigate(['/policies', this.tab, this.summary()?.policyGuid]);
        }
      }

      // Load agencies for admins when summary is available
      const submissionGuid = this.summary()?.submissionGuid;
      if (this.authStore.isAdmin() && submissionGuid) {
        this.lookupStore.loadAgenciesBySubmission(submissionGuid);
      }

      if (this.agencyId()) {
        const agencyId = this.agencyId() ? Number(this.agencyId()) : undefined;
        this.lookupStore.loadLookup('agents', agencyId);
      }
      const programName = this.summary()?.programName;
      const friendlyStatus = this.summary()?.friendlyStatus;
      const agencyName = this.summary()?.agencyName;
      const producerName = this.summary()?.producerName;
      const lastUpdatedBy = this.summary()?.lastUpdatedBy;
      const lastUpdatedByRole = this.summary()?.lastUpdatedByRole;

      const dateStr = this.summary()?.lastModifiedDate;
      const date = new Date(dateStr ?? '');
      const formattedDate =
        `${date.getMonth() + 1}`.padStart(2, '0') +
        '/' +
        `${date.getDate()}`.padStart(2, '0') +
        '/' +
        date.getFullYear();

      // Check if programName and status are available
      if (programName && friendlyStatus && agencyName && producerName) {
        this.toolbarTiles = [
          { iconName: this.notificationIcon, label: programName, data: `Status: ${friendlyStatus}` },
          { iconName: this.submissionsIcon, label: `Agency: ${agencyName}`, data: `Producer: ${producerName}` },
          {
            iconName: this.changeRequestsIcon,
            label: `Last Updated: ${formattedDate}`,
            data: `${lastUpdatedByRole}: ${lastUpdatedBy}`,
          },
        ];
      }
    });
  }

  ngOnInit(): void {
    // Load agencies for non-admin users immediately
    if (!this.authStore.isAdmin() && !this.authStore.isLossRunProcessor()) {
      this.lookupStore.loadLookup('agencies');
    }
    // For admins, agencies are loaded in the effect after summary is available

    this.tab = this.route.snapshot.paramMap.get('tab');
    this.id = this.route.snapshot.paramMap.get('id');

    this.baseUrl = `${this.route.snapshot.url[0].path}`;
    this.isPolicyPage = this.baseUrl == 'policies';
    this.backText.set(this.isPolicyPage ? 'Back to Policies' : 'Back to Submissions');
    this.tabEnum = this.authStore.isLossRunProcessor() ? LossRunProcessorTabs : this.isPolicyPage ? PolicyTabs : SubmissionTabs;
    this.navToNewTab(this.tab as keyof typeof SubmissionTabs | keyof typeof PolicyTabs);
    this.viewSubmissionStore.getSubmissionSummary(this.id ?? '', this.isPolicyPage);
  }

  ngOnDestroy(): void {
    this.viewSubmissionStore.reset();
  }

  navToNewTab(tab: keyof typeof SubmissionTabs | keyof typeof PolicyTabs): void {
    const tabGroup = this.tabGroup();
    if (!tabGroup) {
      return;
    }
    if (this.authStore.isLossRunProcessor()) {
      tabGroup.selectedIndex = LossRunProcessorTabs[tab as keyof typeof LossRunProcessorTabs] ?? LossRunProcessorTabs.lossruns;
    } else if (this.isPolicyPage) {
      tabGroup.selectedIndex = PolicyTabs[tab as keyof typeof PolicyTabs];
    } else {
      tabGroup.selectedIndex = SubmissionTabs[tab as keyof typeof SubmissionTabs];
    }
  }

  onTabChange(event: MatTabChangeEvent): void {
    const newTab = this.tabEnum[event.index];
    const newUrl = `${this.baseUrl}/${newTab}/${this.id}`;
    this.location.go(newUrl);
  }

  changeTab(tab: string) {
    (document.activeElement as HTMLElement)?.blur();
    this.navToNewTab(tab as keyof typeof SubmissionTabs | keyof typeof PolicyTabs);
  }

  goBack() {
    this.router.navigate([this.baseUrl]);
  }

  // Disable logic for split button
  // These might be better if they were computed signals.
  menuItemDisabled(btnName: string): boolean {
    if (btnName === 'Issue Policy') {
      if (this.summary()?.status?.includes('Declined') || this.summary()?.status === 'Indication') {
        return true;
      }
      return false;
    }
    if (btnName == 'Bind Request') {
      if (this.summary()?.status === 'Quoted' || this.summary()?.status === 'Producer Review') {
        return false;
      }
      return true;
    }
    return false;
  }
  isMainButtonDisabled(): boolean {
    const status = this.summary()?.status;
    if (this.mainSplitButtonText() === 'Manage') {
      if (this.authStore.isAdmin()) {
        return false;
      } else {
        return status === 'Quoted' || status === 'Bind Request' || status === 'Declined';
      }
    }
    return false;
  }

  bindRequest() {
    const modalOptions = {
      modalType: ModalType.FullScreen,
      data: {
        submissionGuid: this.id,
        subTitle: `${this.summary()?.businessInfo?.businessName} | ${this.summary()?.submissionNumber}`,
        policyEffectiveDate: this.summary()?.policyEffectiveDate,
        retailAgentId: (this.summary() as any)?.retailAgentId,
      },
    };
    this.modalService.open(BindRequestComponent, modalOptions, (result) => {
      if (result) {
        this.viewSubmissionStore.getSubmissionSummary(this.id ?? '', this.isPolicyPage);
        this.viewSubmissionStore.getSubmissionDocuments(this.id ?? '', this.isPolicyPage);
      }
    });
  }

  updateProducer() {
    const summary = this.summary();
    const retailAgentId = (summary as any)?.retailAgentId;

    const modalOptions = {
      modalType: retailAgentId ? ModalType.Medium : ModalType.Large,
      data: {
        retailAgentId: retailAgentId,
        agencyName: summary?.agencyName,
        producerName: summary?.producerName,
        agencyAddress1: summary?.agencyAddress1,
        agencyAddress2: summary?.agencyAddress2,
        agencyCity: summary?.agencyCity,
        agencyStateCode: summary?.agencyStateCode,
        agencyZipCode: summary?.agencyZipCode,
        agencies: this.lookupStore.agencies?.() ?? [],
        producers: this.lookupStore.agents?.() ?? [],
      },
    };

    this.modalService.open(UpdateProducerModalComponent, modalOptions, (result) => {
      if (!result) {
        return;
      }
      this.loading.set(true);
      const submissionGuid = this.summary()?.submissionGuid;
      if (!submissionGuid) {
        throw new Error('Submission GUID is undefined.');
      }

      // result can be either { producerId: number } or { agencyName: string, producerName: string, ... }
      this.viewSubmissionStore
        .updateSubmissionProducer(submissionGuid, result)
        .then(() => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: { toastType: ToastType.Success, message: 'The submission producer has been updated.' },
          });
        })
        .catch((error) => {
          console.error('Error archiving submission:', error);
          this.snackbar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: {
              toastType: ToastType.Error,
              message: 'An error occurred while updating the submission producer.',
            },
          });
        })
        .finally(() => {
          this.viewSubmissionStore.getSubmissionSummary(this.id ?? '', this.isPolicyPage);
          this.loading.set(false);
        });
    });
  }

  archive() {
    const modalOptions = {
      modalType: ModalType.Medium,
      data: {
        submissionNumber: this.summary()?.submissionNumber,
        agencyName: this.summary()?.agencyName,
      },
    };

    this.modalService.open(ArchiveSubmissionModalComponent, modalOptions, (success) => {
      if (success) {
        this.loading.set(true);
        this.viewSubmissionStore
          .updateSubmissionStatus(this.id ?? '', SubmissionStatusEnum.Archive)
          .then(() => {
            this.snackbar.openFromComponent(SnackbarComponent, {
              duration: 5000,
              data: { toastType: ToastType.Success, message: 'Your submission was archived.' },
            });
          })
          .catch((error) => {
            console.error('Error archiving submission:', error);
            this.snackbar.openFromComponent(SnackbarComponent, {
              duration: 5000,
              data: {
                toastType: ToastType.Error,
                message: 'An error occurred while archiving the submission.',
              },
            });
          })
          .finally(() => {
            this.router.navigate(['/submissions']);
            this.loading.set(false);
          });
      }
    });
  }

  editQuote() {
    // LIO keeps its classifications/payroll on locations[].liabilityClasses, which the RCIP wizard can't
    // render — send LIO submissions to their own wizard.
    this.router.navigate([this.isLio() ? '/lio-quote' : '/quote', this.id]);
  }

  reviewSubmission() {
    const data = {
      id: this.id,
      status: this.summary()?.status,
    };
    this.modalService.open(SubmissionReviewModalComponent, {
      modalType: ModalType.FullScreen,
      data: data,
    });
  }

  export() {
    //tbd
  }

  issuePolicy() {
    const summary = this.summary() as any;

    // Helper function to treat '-' as empty value
    const normalizeValue = (value: string | null | undefined) => {
      return value === '-' ? '' : value;
    };

    // Use Large modal if Agency/Producer section will be shown, otherwise use Medium
    const modalType = summary?.retailAgentId ? ModalType.Medium : ModalType.Large;

    const modalOptions = {
      modalType: modalType,
      data: {
        id: this.id,
        subtitle: `${this.summary()?.businessInfo?.businessName} - ${this.summary()?.submissionNumber}`,
        effectiveDate: this.summary()?.policyEffectiveDate,
        retailAgentId: summary?.retailAgentId,
        agencyName: normalizeValue(summary?.agencyName),
        producerName: normalizeValue(summary?.producerName),
        agencyAddress1: summary?.agencyAddress1,
        agencyAddress2: summary?.agencyAddress2,
        agencyCity: summary?.agencyCity,
        agencyStateCode: summary?.agencyStateCode,
        agencyZipCode: summary?.agencyZipCode,
      },
    };
    this.modalService.open(PriorWorkExclusionModalComponent, modalOptions, (data?: any) => {
      if (data) {
        this.viewSubmissionStore.issuePolicy(this.id!, data).then(() => {
          this.viewSubmissionStore.getSubmissionSummary(this.id ?? '', this.isPolicyPage);
          this.snackbar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: { toastType: ToastType.Success, message: 'Your policy was issued.' },
          });
        });
      }
    });
  }

  openAddEndorsementModal(policyChangeType: string) {
    const data = {
      policyGuid: this.id,
      policyNumber: this.summary()?.policyNumber,
      insuredName: this.summary()?.businessInfo?.businessName,
      policyChangeType: policyChangeType,
    };
    this.modalService.open(
      PolicyEndorsementFormComponent,
      {
        // Todo: fix modal sizes. Small is bigger than medium.
        modalType: ModalType.Small,
        data: data,
      },
      (result) => {
        if (result) {
          this.viewSubmissionStore.getSubmissionSummary(this.id!, this.isPolicyPage);
          const params = {
            policyGuid: this.id,
            pageSize: 2147483647,
            pageNumber: 1,
            dateFrom: '',
            dateTo: '',
          };
          this.viewSubmissionStore.getEndorsements(params);
        }
      },
    );
  }
}
